import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useKernel } from "@/kernel/useKernel";
import CodeWorkspace from "@/components/CodeWorkspace";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import Icon from "@/components/Icon";
import Logo from "@/components/Logo";

const STARTER = `# Environnement de développement libre — écris ton code ici, puis « Exécuter ».
print("Bonjour !")
`;

/**
 * Bac à sable : même atelier de code que les exercices, mais sans exercice ni
 * persistance. Tout est perdu à la fermeture de l'onglet (avertissement + garde
 * beforeunload). La connexion reste requise (SSO orense, cf. RequireAuth dans App).
 */
export default function Playground() {
  const navigate  = useNavigate();
  const kernel    = useKernel();
  const [code, setCode] = useState(STARTER);
  const [showWarning, setShowWarning] = useState(true);

  // Console propre à l'arrivée (le kernel est un singleton partagé : on évite
  // d'hériter de la sortie d'un exercice précédent).
  useEffect(() => {
    kernel.clearOutput();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Avertit avant de quitter si du code a été écrit (rien n'est sauvegardé ici).
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (code.trim() && code !== STARTER) {
        e.preventDefault();
        e.returnValue = ""; // requis par certains navigateurs pour afficher la confirmation
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [code]);

  return (
    <div style={styles.layout}>
      <div style={styles.topbar}>
        <button style={styles.iconToggle} onClick={() => navigate("/")} aria-label="Retour à l'accueil" title="Retour à l'accueil">
          <Logo size={24} />
        </button>
        <span style={styles.title}>Environnement de développement</span>
        <span style={styles.unsavedBadge} title="Aucune sauvegarde dans le bac à sable">
          <Icon name="status" size={9} style={{ color: "var(--yellow)" }} />
          Non sauvegardé
        </span>
        <span style={{ flex: 1 }} />
        <div style={styles.kernelStatus}>
          {kernel.error ? (
            <span style={styles.statusItem} title={kernel.error}>
              <Icon name="status" size={9} style={{ color: "var(--red)" }} />
              <span style={{ color: "var(--red)" }}>Erreur kernel</span>
            </span>
          ) : kernel.ready ? (
            <span style={styles.statusItem}>
              <Icon name="status" size={9} style={{ color: "var(--green)" }} />
              <span style={{ color: "var(--green)" }}>Prêt</span>
            </span>
          ) : (
            <span style={styles.statusItem}>
              <Icon name="status" size={9} style={{ color: "var(--yellow)" }} />
              <span style={{ color: "var(--yellow)" }}>Chargement Pyodide… (peut prendre 30–60s)</span>
            </span>
          )}
        </div>
        <ThemeToggle />
        <AppLauncher />
      </div>

      {/* Bandeau d'avertissement (refermable) : rien n'est sauvegardé. */}
      {showWarning && (
        <div style={styles.warning}>
          <Icon name="alert" size={16} style={{ color: "var(--yellow)", flexShrink: 0 }} />
          <span style={{ flex: 1 }}>
            Bac à sable : <strong>rien n'est sauvegardé</strong>. Pense à télécharger ton code
            (« Télécharger .py ») — tout sera perdu en fermant ou rechargeant l'onglet.
          </span>
          <button
            style={styles.warningClose}
            onClick={() => setShowWarning(false)}
            aria-label="Fermer l'avertissement"
            title="Fermer"
          >
            <Icon name="clear" size={16} />
          </button>
        </div>
      )}

      <div style={styles.body}>
        <CodeWorkspace code={code} onChange={setCode} downloadFilename="bac_a_sable.py" />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  layout: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    background: "var(--bg-base)",
    overflow: "hidden",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 16px",
    background: "var(--bg-bar)",
    borderBottom: "1px solid var(--border-soft)",
    flexShrink: 0,
  },
  iconToggle: {
    background: "transparent",
    border: "none",
    color: "var(--text)",
    padding: 6,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
  },
  title: { fontWeight: 600, color: "var(--text)" },
  unsavedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-muted)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    padding: "2px 8px",
  },
  kernelStatus: { fontSize: 12 },
  statusItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  warning: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    color: "var(--text)",
    background: "var(--badge-bg)",
    borderBottom: "1px solid var(--border-soft)",
    padding: "8px 16px",
    flexShrink: 0,
  },
  warningClose: {
    background: "transparent",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    padding: 4,
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "var(--radius-sm)",
    flexShrink: 0,
  },
  body: { display: "flex", flex: 1, overflow: "hidden" },
};
